/*
 OTAExample.css
 =========

------------------------------------------------------------------------
 Author:   Lucas Meier                                        2025
 Modifs:

 Goal:   User permissions resolver for TRACE.

   (c) 2025  Lucas Meier   
   --------------------------

   CSEM S.A.
   Jaquet-Droz 1
   CH-2000 Neuchâtel
   http://www.csem.ch


   THIS SOFTWARE IS CONFIDENTIAL AND CANNOT BE DISTRIBUTED
   WITHOUT THE CSEM PRIOR WRITTEN AGREEMENT.

   THIS  SOFTWARE CAN ONLY BE USED IN THE EUROPEAN PROJECT TRACE AND
   SHALL REMAIN PRIVATE AT ALL TIMES.

------------------------------------------------------------------------
*/

import { Auth } from "aws-amplify";
import { injectable } from "inversify";
import awsConfig from "../aws-config.json";

@injectable()
export class UserService {
    public async getSession() {
        return Auth.currentSession();
    }

    public async getUser() {
        return Auth.currentAuthenticatedUser();
    }

    public async getAttributes() {
        const user = this.getUser();
        return new Map((await Auth.userAttributes(user)).map(attr => [attr.getName(), attr.getValue()]));
    }

    public async getCognitoGroups(): Promise<string[]> {
        return (await this.getSession()).getIdToken().payload["cognito:groups"] ?? [];
    }

    public async getCognitoRoles(): Promise<string[]> {
        return (await this.getSession()).getIdToken().payload["cognito:roles"] ?? [];
    }

    public async getCognitoEmail(): Promise<string[]> {
        return (await this.getSession()).getIdToken().payload["email"] ?? "";
    }

    public async isInGroup(group: string) {
        return (await this.getCognitoGroups()).includes(group);
    }

    public async isAdmin() {
        if(awsConfig.hasOwnProperty("aws_cognito_group_admin_role_arn")){
            return  (await this.getCognitoRoles()).includes(awsConfig.aws_cognito_group_admin_role_arn);
        }else{
            return false;
        }
    }

    /**
     * If available return the admin role, otherwise a role matching the substring, otherwise just the first role, otherwise the default authenticated role
     */
    public async getFittingRoleArn(roleArnSubstring?: string) {
        
        const availableRoles = await this.getCognitoRoles();
        
        // If we are looking for a specific role we return that.
        if (roleArnSubstring) {
            let role = availableRoles.find(r => r.toLowerCase().includes(roleArnSubstring));
            if (role)
                return role;
        }
        
        // If we have the admin role we return that.
        if(await this.isAdmin()){
            return awsConfig.aws_cognito_group_admin_role_arn ?? "";
        }
        
        // Otherwise we return the first available role.
        if (availableRoles.length > 0) {
            return availableRoles[0];
        }
        
        return awsConfig.aws_cognito_identity_pool_roles.authenticated_arn;
    }
}